import { useToast } from "@chakra-ui/react"
import { useAtom } from "jotai"
import { useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Paths, RETURN_URL, SESSION_EXPIRED_KEY } from "../../common/commonConstants"
import { loginModalOpenAtom, userDetailsAtom, userProfileDataAtom } from "../Menu/menu.store"
import { clearAuthData, getTokenExpiry, isNewLoginUIEnabled, isProtectedRoute } from "../Menu/menu.support"

export const AuthErrorHandler = (): null => {
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [userDetails, setUserDetails] = useAtom(userDetailsAtom)
  const [, setUserProfileData] = useAtom(userProfileDataAtom)
  const [, setLoginModalOpen] = useAtom(loginModalOpenAtom)

  // Proactive session expiry: warn 5 minutes before, then auto-logout
  useEffect(() => {
    if (!userDetails) return

    const expiry = getTokenExpiry()
    if (!expiry) return

    const now = Date.now()
    const msUntilExpiry = expiry - now
    const msUntilWarning = msUntilExpiry - 5 * 60 * 1000

    if (msUntilExpiry <= 0) return

    let warningTimer: ReturnType<typeof setTimeout> | null = null

    if (msUntilWarning > 0) {
      warningTimer = setTimeout(() => {
        toast({
          title: "Session expiring soon",
          description: "Your session expires in 5 minutes and you will need to login again.",
          status: "warning",
          duration: null,
          isClosable: true,
          position: "top"
        })
      }, msUntilWarning)
    }

    const expiryTimer = setTimeout(() => {
      clearAuthData()
      setUserDetails(null)
      setUserProfileData({})

      const currentPath = window.location.pathname

      if (isNewLoginUIEnabled()) {
        if (isProtectedRoute(currentPath)) {
          sessionStorage.setItem(RETURN_URL, currentPath + window.location.search)
          navigate("/", { replace: true })
          setLoginModalOpen(true)
        }
        // On non-protected routes: just clear auth — login button reappears in header
      } else {
        if (isProtectedRoute(currentPath)) {
          navigate(Paths.LOGIN.login, { replace: true })
        }
      }
    }, msUntilExpiry)

    return () => {
      if (warningTimer) clearTimeout(warningTimer)
      clearTimeout(expiryTimer)
    }
  }, [userDetails?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleAuthError = (event: CustomEvent<string>): void => {
      const currentPath = event.detail || location.pathname

      if (currentPath !== Paths.LOGIN.login && currentPath !== Paths.LOGIN.logout && isProtectedRoute(currentPath)) {
        clearAuthData()
        setUserDetails(null)
        setUserProfileData({})

        if (isNewLoginUIEnabled()) {
          sessionStorage.setItem(RETURN_URL, currentPath + window.location.search)
          navigate("/", { replace: true })
          setLoginModalOpen(true)
        } else {
          const currentPathWithQuery = currentPath + window.location.search
          sessionStorage.setItem(RETURN_URL, currentPathWithQuery)
          sessionStorage.setItem(SESSION_EXPIRED_KEY, "true")
          navigate(Paths.LOGIN.login, { replace: true })
        }
      }
    }

    window.addEventListener("auth-error" as any, handleAuthError as EventListener)

    return () => {
      window.removeEventListener("auth-error" as any, handleAuthError as EventListener)
    }
  }, [navigate, location.pathname, setUserDetails, setUserProfileData])

  return null
}

export const triggerAuthError = (currentPath?: string): void => {
  const path = currentPath || window.location.pathname
  const event = new CustomEvent("auth-error", { detail: path })
  window.dispatchEvent(event)
}
